import React, { createContext, useContext, useEffect, useRef, useState, useReducer } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { fetchAuthSession } from 'aws-amplify/auth';
import AuthContext from '../auth/AuthContext';
import HubConfigurationFactory from '../../utility/HubConfigurationFactory';
import ProcessContext from '../process/ProcessContext';
import UIContext from '../ui/UIContext';
import SignalRReducer from './SignalRReducer';
import { SET_SIGNALR_IS_CONNECTED } from '../types/contextTypes';
import { getIgnoreMaintenanceMode } from '../../bridges/maintenanceBridge';
import { MARKETING } from '../../constants/roles/roles';

/**
 * Context for managing SignalR connections and providing SignalR methods to components.
 */
const SignalRContext = createContext();

/**
 * Starts a SignalR connection and handles reconnection logic.
 */
const startSignalRConnection = async (
    connection,
    notifySuccess,
    notifyError,
    removeError,
    setSignalRConnectionStatus
) => {
    try {
        const match = connection.baseUrl.match(/\/hubs\/v1\/([^?]+)/);
        const hub = match ? match[1] : 'unknown hub';
        console.log(`Attempting to start SignalR connection to ${hub}...`);

        await connection.start();

        if (connection.state === 'Connected') {
            setSignalRConnectionStatus(true);
            console.log(`Connected successfully to ${hub}`);
        } else {
            console.warn(`Failed to connect to ${connection.baseUrl}`);
            setSignalRConnectionStatus(false);
        }

        connection.onreconnecting((error) => {
            setSignalRConnectionStatus(false);
            console.error(`Connection lost: ${error?.message}`);
            notifyError(
                'Connection lost. Attempting to reconnect… Make sure you have a stable internet connection.'
            );
        });

        connection.onreconnected(() => {
            removeError();
            setSignalRConnectionStatus(true);
            console.log('Reconnected successfully.');
            notifySuccess('Connection successfully reestablished');
        });
    } catch (error) {
        console.error('Error while starting SignalR connection:', error);
    }
};

/**
 * SignalRProvider component initializes SignalR connections for the given hubs and provides
 * a context for interacting with SignalR methods.
 */
export const SignalRProvider = React.memo(({ children }) => {
    const initialState = {
        signalRIsConnected: false,
    };

    const [state, dispatch] = useReducer(SignalRReducer, initialState);
    const connectionsRef = useRef({});
    const [accessToken, setAccessToken] = useState(null);
    const [signalRIsReady, setSignalRIsReady] = useState(false);
    const [hubConfiguration, setHubConfiguration] = useState(null);

    const { notifyError, notifySuccess } = useContext(ProcessContext);
    const { removeError, addBanner } = useContext(UIContext);
    const { currentTenant, currentContainer, accessibleContainers, currentRoles, isGlobalAdmin } = useContext(AuthContext);

    const maintenanceRedirectTimerRef = useRef(null);
    const maintenancePagePath = `${import.meta.env.BASE_URL}maintenancePage.html`;

    const setSignalRConnectionStatus = (isConnected) => {
        dispatch({
            type: SET_SIGNALR_IS_CONNECTED,
            payload: isConnected,
        });
    };

    const clearMaintenanceRedirectTimer = () => {
        if (maintenanceRedirectTimerRef.current) {
            clearTimeout(maintenanceRedirectTimerRef.current);
            maintenanceRedirectTimerRef.current = null;
        }
    };

    const handleMaintenanceModeChanged = (isMaintenanceModeEnabled) => {
        if (!isMaintenanceModeEnabled) {
            clearMaintenanceRedirectTimer();
            return;
        }

        if (getIgnoreMaintenanceMode()) {
            console.log("Maintenance mode enabled, but bypassed by user.");
            return;
        }

        // Banner stays until redirect
        addBanner('Application just changed to Maintenance. You will be redirected in a few seconds.', false);

        clearMaintenanceRedirectTimer();
        maintenanceRedirectTimerRef.current = window.setTimeout(() => {
            window.location.replace(maintenancePagePath);
        }, 5000);
    };

    const stopAllConnections = async () => {
        clearMaintenanceRedirectTimer();

        for (const hubName in connectionsRef.current) {
            const connection = connectionsRef.current[hubName]?.current;
            if (connection) {
                console.log(`Stopping connection for hub: ${hubName}`);
                await connection.stop();
                delete connectionsRef.current[hubName];
            }
        }
    };

    useEffect(() => {
        removeError();
    }, [currentTenant]);

    /**
     * Fetches the access token from AWS Amplify and sets it in the state.
     */
    useEffect(() => {
        const fetchAccessToken = async () => {
            try {
                const session = await fetchAuthSession();
                const newToken = session.tokens?.accessToken?.toString();
                setAccessToken(newToken ?? null);

                if (newToken) {
                    console.log('AccessToken updated.');
                }
            } catch (err) {
                console.error('Failed to get access token:', err);
            }
        };

        fetchAccessToken();
    }, []);



    useEffect(() => {
        const factory = new HubConfigurationFactory({
            tenantId: currentTenant?.id,
            containerId: currentContainer?.id,
            scaleId: currentContainer?.scaleId,
        });

        const newConfiguration = factory.getConfiguration();
        if (JSON.stringify(newConfiguration) !== JSON.stringify(hubConfiguration)) {
            setHubConfiguration(newConfiguration);
            console.log('Hub configuration updated.');
        }
    }, [currentContainer, currentTenant]);

    useEffect(() => {
        if (accessToken && hubConfiguration && hubConfiguration.length > 0) {
            setSignalRIsReady(true);
        }
    }, [accessToken, hubConfiguration]);

    useEffect(() => {
        if (!signalRIsReady) {
            return;
        }

        const initializeConnections = async () => {
            console.log('Initializing SignalR connections...');

            await stopAllConnections();

            for (const hub of hubConfiguration) {
                const isApplicationStateHub = hub.name === 'applicationStateHub';
                const isScaleHub = hub.name === 'scaleHub';
                const isOnlyMarketingUser = currentRoles?.length === 1 && currentRoles[0] === MARKETING && !isGlobalAdmin;

                // Only non-state hubs require container prerequisites
                if (!isApplicationStateHub) {
                    if (accessibleContainers?.length < 1 || !currentContainer) return;
                    if (!currentContainer?.id || !currentContainer?.scaleId) {
                      notifyError(<>Issue loading container data. Please relogin.</>);
                      return;
                    }
                }
                
                // Skip initializing scaleHub for marketing users, because they don't have access to the processes
                if (isScaleHub && isOnlyMarketingUser) {
                    continue;
                }

                const offset = Math.floor(Math.random() * 250 + 250);

                const connection = new HubConnectionBuilder()
                    .withUrl(import.meta.env.VITE_FARMER_API_URL + hub.url, {
                        accessTokenFactory: () => accessToken,
                    })
                    .configureLogging(LogLevel.Information)
                    .withAutomaticReconnect({
                        nextRetryDelayInMilliseconds: (retryContext) => {
                            const retryIntervals = [
                                1000 + offset,
                                5000 + offset,
                                10000 + offset,
                                20000 + offset,
                                30000 + offset,
                            ];
                            return retryContext.previousRetryCount < retryIntervals.length
                                ? retryIntervals[retryContext.previousRetryCount]
                                : 30000 + offset;
                        },
                    })
                    .build();

                // Register handlers AFTER connection exists
                if (isApplicationStateHub) {
                    connection.on('MaintenanceModeChanged', handleMaintenanceModeChanged);
                }

                connection.serverTimeoutInMilliseconds = 15000;
                connection.keepAliveIntervalInMilliseconds = 3000;

                connectionsRef.current[hub.name] = { current: connection };
                await startSignalRConnection(
                    connection,
                    notifySuccess,
                    notifyError,
                    removeError,
                    setSignalRConnectionStatus
                );
            }
        };

        initializeConnections();

        return () => {
            stopAllConnections();
        };
    }, [hubConfiguration, signalRIsReady, currentRoles, isGlobalAdmin]);

    const registerClientFunction = (hubName, functionName, callback) => {
        const connection = connectionsRef.current[hubName];
        if (connection?.current) {
            connection.current.on(functionName, callback);
            console.log(`Registering ${functionName} to ${hubName}`);
        }
    };

    const unregisterClientFunction = (hubName, functionName, callback) => {
        const connection = connectionsRef.current[hubName];
        if (connection?.current) {
            connection.current.off(functionName, callback);
            console.log(`Unregistering ${functionName} from ${hubName}`);
        }
    };

    const invokeServerMethod = async (hubName, methodName, ...args) => {
        const connection = connectionsRef.current[hubName];
        if (connection?.current) {
            try {
                await connection.current.invoke(methodName, ...args);
            } catch (err) {
                console.error(`Error invoking ${methodName}:`, err.toString());
            }
        }
    };

    const isHubConnected = (hubName) => {
        return connectionsRef.current[hubName]?.current?.state === 'Connected';
    };

    return (
        <SignalRContext.Provider
            value={{
                registerClientFunction,
                unregisterClientFunction,
                invokeServerMethod,
                signalRIsReady,
                signalRIsConnected: state.signalRIsConnected,
                isHubConnected,
            }}
        >
            {children}
        </SignalRContext.Provider>
    );
});

export const useSignalRContext = () => useContext(SignalRContext);
